"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { cn } from "@/lib/utils";
type TrialBlurGateProps = {
  /** Whether the user is on trial */
  isTrial: boolean;
  /** The account ID for upgrade modal context */
  accountId?: string;
  /** Content to blur/lock */
  children: React.ReactNode;
  /** Optional placeholder/demo content to show blurred instead of actual children */
  placeholder?: React.ReactNode;
  /** Custom CTA text (default: "Unlock analytics") */
  ctaText?: string;
  /** Custom title text (default: "Upgrade to unlock") */
  title?: string;
  /** Custom description text */
  description?: string;
  /** Minimum height for the container */
  minHeight?: string;
  /** Additional class names for the wrapper */
  className?: string;
};

/**
 * TrialBlurGate - Blurs analytics content for trial users with upgrade CTA
 *
 * Usage:
 * <TrialBlurGate isTrial={account?.account_type === "trial"} accountId={account?.id}>
 *   <ExpensiveAnalyticsComponent />
 * </TrialBlurGate>
 *
 * With placeholder:
 * <TrialBlurGate isTrial={isTrial} placeholder={<DemoData />}>
 *   <RealAnalyticsComponent />
 * </TrialBlurGate>
 */
export default function TrialBlurGate(t0) {
  const $ = _c(29);
  const {
    isTrial,
    accountId,
    children,
    placeholder,
    ctaText: t1,
    title: t2,
    description,
    minHeight,
    className
  } = t0;
  const ctaText = t1 === undefined ? "Unlock analytics" : t1;
  const title = t2 === undefined ? "Upgrade to unlock" : t2;
  const {
    openUpgradeModal
  } = useModalContext();
  if (!isTrial) {
    let t3;
    if ($[0] !== children) {
      t3 = <>{children}</>;
      $[0] = children;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    return t3;
  }
  let t3;
  if ($[2] !== className) {
    t3 = cn("relative", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== minHeight) {
    t4 = minHeight ? {
      minHeight
    } : undefined;
    $[4] = minHeight;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = placeholder || children;
  let t6;
  if ($[6] !== t5) {
    t6 = <div className="pointer-events-none select-none blur-sm" aria-hidden="true">{t5}</div>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"><LockClosedIcon className="h-6 w-6 text-gray-400" /></div>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== title) {
    t8 = <p className="text-base font-medium text-gray-900">{title}</p>;
    $[9] = title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== description) {
    t9 = description && <p className="mt-1 max-w-[200px] text-[13px] text-gray-500">{description}</p>;
    $[11] = description;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== accountId || $[14] !== openUpgradeModal) {
    t10 = () => openUpgradeModal(accountId);
    $[13] = accountId;
    $[14] = openUpgradeModal;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("mt-4 inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5", "bg-[rgb(255,163,209)] text-sm font-bold text-gray-900", "transition-all duration-200", "hover:bg-[#fc83bf] hover:shadow-lg hover:shadow-pink-500/20", "focus:outline-none focus:ring-2 focus:ring-pink-200", "active:scale-[0.98]");
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== ctaText || $[18] !== t10) {
    t12 = <button onClick={t10} className={t11}>{ctaText}</button>;
    $[17] = ctaText;
    $[18] = t10;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t8 || $[22] !== t9) {
    t13 = <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60 backdrop-blur-[2px]"><div className="flex flex-col items-center px-4 text-center">{t7}{t8}{t9}{t12}</div></div>;
    $[20] = t12;
    $[21] = t8;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t3 || $[26] !== t4 || $[27] !== t6) {
    t14 = <div className={t3} style={t4}>{t6}{t13}</div>;
    $[24] = t13;
    $[25] = t3;
    $[26] = t4;
    $[27] = t6;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}